import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  BookOpen, Flame, Trophy, Sparkles, Lock, Trash2, Check, RotateCcw, Minus, Plus, Share2, Loader2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import ShareGardenDialog from '../components/ShareGardenDialog';
import AmbassadorPanel from '../components/AmbassadorPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RARITY_META = {
  common:   { ring: '#a3a3a3', label: 'Gewoon'    },
  uncommon: { ring: '#6ea85f', label: 'Bijzonder' },
  rare:     { ring: '#3b82f6', label: 'Zeldzaam'  },
  epic:     { ring: '#b76eff', label: 'Magisch'   },
};

const ICON_MAP = {
  'book-open': BookOpen, book: BookOpen, library: BookOpen,
  flame: Flame, trophy: Trophy, sparkles: Sparkles, crown: Trophy,
  flower: Sparkles, wand: Sparkles,
};

function uid() {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

export default function Garden() {
  const { token, user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
  const [garden, setGarden] = useState(null);
  const [placed, setPlaced] = useState([]);   // [{slug,x,y,instance_id,scene}]
  const [selectedSlug, setSelectedSlug] = useState(null);  // slug "pending" to drop
  const [selectedPlacedId, setSelectedPlacedId] = useState(null); // currently "picked" placed item
  const [shareOpen, setShareOpen] = useState(false);
  const [activeScene, setActiveScene] = useState('garden'); // 'garden' | 'house'
  const sceneRef = useRef(null);
  const saveTimerRef = useRef(null);
  const initializedRef = useRef(false);
  const placedRef = useRef([]);

  const headers = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const fetchGarden = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/garden`, { headers });
      setGarden(res.data);
      const initialPlaced = res.data.placed || [];
      setPlaced(initialPlaced);
      placedRef.current = initialPlaced;
      initializedRef.current = true;
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    if (isAuthenticated) fetchGarden();
    else setLoading(false);
  }, [isAuthenticated, fetchGarden]);

  // Houd ref synchroon zodat de debounced save altijd de laatste state stuurt
  useEffect(() => { placedRef.current = placed; }, [placed]);

  // Debounced autosave: 800ms na de laatste wijziging
  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaveStatus('saving');
    saveTimerRef.current = setTimeout(async () => {
      try {
        await axios.put(
          `${API}/garden/layout`,
          { placed: placedRef.current },
          { headers },
        );
        setSaveStatus('saved');
        // Verberg "Opgeslagen" indicator na 1.5s -> idle
        setTimeout(() => {
          setSaveStatus((s) => (s === 'saved' ? 'idle' : s));
        }, 1500);
      } catch (e) {
        console.error('Autosave fout', e);
        setSaveStatus('error');
        toast.error('Opslaan mislukt — controleer je verbinding');
      }
    }, 800);
  }, [headers]);

  // Cleanup timer bij unmount + flush eventuele wijzigingen
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        // Flush direct (fire-and-forget) zodat laatste wijziging niet verloren gaat
        if (initializedRef.current && token) {
          axios.put(
            `${API}/garden/layout`,
            { placed: placedRef.current },
            { headers: { Authorization: `Bearer ${token}` } },
          ).catch(() => {});
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const usageBySlug = useMemo(() => {
    const m = {};
    placed.forEach((p) => { m[p.slug] = (m[p.slug] || 0) + 1; });
    return m;
  }, [placed]);

  const remaining = useCallback((slug) => {
    const owned = (garden?.collected?.[slug]) || 0;
    return owned - (usageBySlug[slug] || 0);
  }, [garden, usageBySlug]);

  const handleScenePointerDown = (e) => {
    // Negeer als de pointer op een reeds geplaatst item begon (dat item handelt zelf af)
    if (e.target.closest('[data-placed="1"]')) return;
    // Scene-tap sluit ook de selectie van een geplaatst item
    setSelectedPlacedId(null);
    if (!selectedSlug) return;
    const rect = sceneRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    if (remaining(selectedSlug) <= 0) {
      toast.error('Je hebt dit item al helemaal geplaatst');
      return;
    }
    setPlaced((prev) => [...prev, { slug: selectedSlug, x, y, instance_id: uid(), scene: activeScene }]);
    scheduleSave();
    if (remaining(selectedSlug) - 1 <= 0) setSelectedSlug(null);
  };

  const movePlaced = (instance_id, x, y) => {
    setPlaced((prev) => prev.map((p) => (p.instance_id === instance_id ? { ...p, x, y } : p)));
    scheduleSave();
  };

  const resizePlaced = (instance_id, scale) => {
    setPlaced((prev) => prev.map((p) => (p.instance_id === instance_id ? { ...p, scale } : p)));
    scheduleSave();
  };

  const removePlaced = (instance_id) => {
    setPlaced((prev) => prev.filter((p) => p.instance_id !== instance_id));
    scheduleSave();
  };

  const resetLayout = () => {
    setPlaced([]);
    scheduleSave();
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 pt-24 pb-28">
        <div className="text-center max-w-md">
          <div className="text-5xl mb-3">🏡</div>
          <h1 className="font-display text-3xl text-foreground mb-3">Oma's Tuintje</h1>
          <p className="text-muted-foreground mb-5 text-sm">
            Log in om verhalen te lezen, cadeautjes te verdienen en je eigen tuintje te versieren.
          </p>
          <a href="/login">
            <Button size="lg" className="rounded-full bg-old-rose text-night-blue font-bold">
              Inloggen
            </Button>
          </a>
        </div>
      </div>
    );
  }

  if (loading || !garden) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  const { catalog = [], collected = {}, badges = [], badge_defs = [], current_streak = 0, best_streak = 0, stories_completed = 0, total_rewards = 0, ambassador_count = 0, ambassador_milestones = [] } = garden;
  const earnedBadges = new Set(badges);

  return (
    <div className="min-h-screen pt-20 pb-28 md:pb-10 px-3 md:px-6 max-w-7xl mx-auto" data-testid="garden-page">
      {/* Header stats */}
      <div className="mb-4 flex flex-wrap items-center gap-2 md:gap-3">
        <h1 className="font-display text-3xl md:text-4xl text-foreground mr-auto">
          Mijn Tuintje
        </h1>
        <Button
          data-testid="open-share"
          onClick={() => setShareOpen(true)}
          disabled={placed.length === 0}
          size="sm"
          className="rounded-full bg-old-rose hover:bg-old-rose/90 text-night-blue font-bold"
        >
          <Share2 className="w-4 h-4 mr-2" /> Delen
        </Button>
        <StatChip icon={<BookOpen className="w-4 h-4" />} label="Verhalen" value={stories_completed} testid="stat-stories" />
        <StatChip icon={<Flame className="w-4 h-4" />} label="Streak" value={current_streak} testid="stat-streak" />
        <StatChip icon={<Trophy className="w-4 h-4" />} label="Beste" value={best_streak} testid="stat-best" />
        <StatChip icon={<Sparkles className="w-4 h-4" />} label="Kadootjes" value={total_rewards} testid="stat-rewards" />
      </div>

      <div className="grid md:grid-cols-[1fr_320px] gap-4">
        {/* SCENE */}
        <div className="space-y-3">
          {/* Scene toggle: Tuin / Huis */}
          <div className="flex items-center gap-2 p-1 bg-muted/40 rounded-full w-fit" data-testid="scene-toggle">
            <button
              onClick={() => { setActiveScene('garden'); setSelectedSlug(null); setSelectedPlacedId(null); }}
              className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${activeScene === 'garden' ? 'bg-old-rose text-night-blue shadow' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="scene-toggle-garden"
            >
              🌿 Tuin
            </button>
            <button
              onClick={() => { setActiveScene('house'); setSelectedSlug(null); setSelectedPlacedId(null); }}
              className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${activeScene === 'house' ? 'bg-old-rose text-night-blue shadow' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="scene-toggle-house"
            >
              🏠 Huis
            </button>
          </div>
          <p className="text-xs font-accent text-muted-foreground px-1">
            Kies rechts een item, tik {activeScene === 'garden' ? 'in de tuin' : 'in de kamer'} om te plaatsen. Sleep items om te verplaatsen — tik erop voor resize & verwijderen.
          </p>
          <div
            ref={sceneRef}
            onPointerDown={handleScenePointerDown}
            className={`relative w-full aspect-[16/10] rounded-2xl overflow-hidden border border-border shadow-lg ${selectedSlug ? 'cursor-crosshair' : ''}`}
            style={{
              backgroundImage: activeScene === 'house'
                ? `url('/garden-items/house_bg.png'), linear-gradient(180deg,#f5e9d5 0%,#e6d4b8 100%)`
                : `url('/garden-items/scene_bg.png'), linear-gradient(180deg,#cfe9ff 0%,#eaf6c5 55%,#d8e7a9 100%)`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              touchAction: 'none',
            }}
            data-testid="garden-scene"
          >
            {placed.filter((p) => (p.scene || 'garden') === activeScene).map((p) => (
              <PlacedItem
                key={p.instance_id}
                item={p}
                sceneRef={sceneRef}
                selected={selectedPlacedId === p.instance_id}
                onSelect={() => setSelectedPlacedId((id) => (id === p.instance_id ? null : p.instance_id))}
                onMove={(x, y) => movePlaced(p.instance_id, x, y)}
                onResize={(scale) => resizePlaced(p.instance_id, scale)}
                onRemove={() => { removePlaced(p.instance_id); setSelectedPlacedId(null); }}
              />
            ))}
            {selectedSlug && (
              <div className="absolute top-2 left-2 right-2 bg-card/90 backdrop-blur rounded-xl px-3 py-2 text-xs font-accent text-foreground shadow">
                Tik om <b>{catalog.find(c => c.slug === selectedSlug)?.name}</b> te plaatsen · nog {remaining(selectedSlug)} over
                <button
                  onClick={() => setSelectedSlug(null)}
                  className="ml-2 text-muted-foreground underline"
                  data-testid="cancel-placement"
                >
                  annuleren
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <div
              data-testid="save-status"
              className="flex items-center gap-1.5 text-xs font-accent text-muted-foreground min-h-[32px]"
            >
              {saveStatus === 'saving' && (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Opslaan...</>
              )}
              {saveStatus === 'saved' && (
                <><Check className="w-3.5 h-3.5 text-green-600" /> <span className="text-green-700">Opgeslagen</span></>
              )}
              {saveStatus === 'error' && (
                <span className="text-destructive">Opslaan mislukt — probeer opnieuw</span>
              )}
              {saveStatus === 'idle' && (
                <span className="text-muted-foreground/70">Wijzigingen worden automatisch bewaard</span>
              )}
            </div>
            <div className="ml-auto" />
            <Button
              onClick={resetLayout}
              variant="ghost"
              size="sm"
              className="rounded-full"
              data-testid="reset-garden"
            >
              <RotateCcw className="w-4 h-4 mr-2" /> Leegmaken
            </Button>
          </div>
        </div>

        {/* SIDEBAR: Ambassador + Inventory + Badges */}
        <div className="space-y-4">
          <AmbassadorPanel
            count={ambassador_count}
            milestones={ambassador_milestones}
            onInvite={() => setShareOpen(true)}
          />
          {/* Inventory */}
          <div className="bg-card border border-border rounded-2xl p-3" data-testid="inventory-panel">
            <div className="flex items-center justify-between mb-2">
              <div className="font-display text-lg text-foreground">Verzameling</div>
              <div className="text-[10px] text-muted-foreground font-accent">
                {activeScene === 'garden' ? '🌿 Tuin-items' : '🏠 Huis-items'}
              </div>
            </div>
            {catalog.length === 0 ? (
              <div className="text-xs text-muted-foreground">Nog geen items</div>
            ) : (
              <div className="grid grid-cols-3 gap-2 max-h-[420px] overflow-y-auto pr-1">
                {catalog
                  .filter((c) => {
                    const s = c.scene || 'garden';
                    return s === 'any' || s === activeScene;
                  })
                  .map((c) => {
                  const owned = collected[c.slug] || 0;
                  const left = owned - (usageBySlug[c.slug] || 0);
                  const locked = owned === 0;
                  const meta = RARITY_META[c.rarity] || RARITY_META.common;
                  const isSelected = selectedSlug === c.slug;
                  return (
                    <button
                      key={c.slug}
                      disabled={locked || left <= 0}
                      onClick={() => setSelectedSlug(c.slug)}
                      data-testid={`inv-${c.slug}`}
                      className={`relative rounded-xl p-2 text-center transition-all ${isSelected ? 'bg-gold/25' : 'bg-muted/40 hover:bg-muted'} ${locked ? 'opacity-40 cursor-not-allowed' : left <= 0 ? 'opacity-50' : 'cursor-pointer'}`}
                      style={{
                        border: `1.5px solid ${isSelected ? '#db8f2d' : meta.ring}`,
                      }}
                    >
                      <div className="w-full aspect-square flex items-center justify-center">
                        {locked ? (
                          <Lock className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <img
                            src={`/garden-items/${c.slug}.png`}
                            alt={c.name}
                            className="w-full h-full object-contain"
                          />
                        )}
                      </div>
                      <div className="text-[10px] mt-1 font-medium text-foreground truncate">
                        {c.name}
                      </div>
                      {!locked && (
                        <div className="absolute -top-1 -right-1 rounded-full bg-old-rose text-night-blue text-[10px] font-bold w-5 h-5 flex items-center justify-center">
                          {left}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Badges */}
          <div className="bg-card border border-border rounded-2xl p-3" data-testid="badges-panel">
            <div className="font-display text-lg mb-2 text-foreground">Badges</div>
            <div className="grid grid-cols-2 gap-2">
              {badge_defs.map((b) => {
                const earned = earnedBadges.has(b.slug);
                const Icon = ICON_MAP[b.icon] || Sparkles;
                return (
                  <div
                    key={b.slug}
                    data-testid={`badge-${b.slug}`}
                    className={`flex items-center gap-2 p-2 rounded-lg border text-[11px] ${earned ? 'bg-gold/10 border-gold/40 text-foreground' : 'bg-muted/30 border-border text-muted-foreground opacity-60'}`}
                  >
                    <Icon className={`w-4 h-4 shrink-0 ${earned ? 'text-gold' : ''}`} />
                    <div className="leading-tight">
                      <div className="font-bold">{b.name}</div>
                      <div className="text-[10px]">{b.description}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <ShareGardenDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        placed={placed}
        catalog={catalog}
        stats={{
          stories_completed,
          current_streak,
          total_rewards,
        }}
        userId={user?.id}
        siteUrl="omasverhaaltjes.nl"
      />
    </div>
  );
}

function StatChip({ icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-card border border-border text-xs shadow-sm">
      <span className="text-gold">{icon}</span>
      <span className="font-bold text-foreground">{value}</span>
      <span className="text-muted-foreground">{label}</span>
    </div>
  );
}

function PlacedItem({ item, sceneRef, selected, onSelect, onMove, onResize, onRemove }) {
  const rootRef = useRef(null);
  const dragInfo = useRef(null); // { startX, startY, origX, origY, moved }
  const scale = item.scale || 1;

  const handlePointerDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!sceneRef.current) return;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
    dragInfo.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: item.x,
      origY: item.y,
      moved: false,
    };
  };

  const handlePointerMove = (e) => {
    if (!dragInfo.current || !sceneRef.current) return;
    const dx = e.clientX - dragInfo.current.startX;
    const dy = e.clientY - dragInfo.current.startY;
    // threshold om tap vs drag te onderscheiden
    if (!dragInfo.current.moved && Math.hypot(dx, dy) < 6) return;
    dragInfo.current.moved = true;
    const rect = sceneRef.current.getBoundingClientRect();
    const newX = Math.max(0, Math.min(100, dragInfo.current.origX + (dx / rect.width) * 100));
    const newY = Math.max(0, Math.min(100, dragInfo.current.origY + (dy / rect.height) * 100));
    onMove(newX, newY);
  };

  const handlePointerUp = (e) => {
    e.stopPropagation();
    if (!dragInfo.current) return;
    const wasMoved = dragInfo.current.moved;
    dragInfo.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
    if (!wasMoved) {
      // echte tap -> selectie toggle
      onSelect();
    }
  };

  return (
    <motion.div
      ref={rootRef}
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0, opacity: 0 }}
      transition={{ type: 'spring', stiffness: 260, damping: 18 }}
      className="absolute"
      style={{
        left: `${item.x}%`,
        top: `${item.y}%`,
        transform: 'translate(-50%, -80%)',
        width: `${14 * scale}%`,
        maxWidth: `${96 * scale}px`,
        touchAction: 'none',
        cursor: 'grab',
        zIndex: selected ? 30 : 10,
      }}
      data-placed="1"
      data-testid={`placed-${item.slug}`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <img
        src={`/garden-items/${item.slug}.png`}
        alt=""
        draggable={false}
        className="w-full h-auto select-none pointer-events-none"
        style={{
          filter: selected
            ? 'drop-shadow(0 0 10px rgba(219,143,45,0.9)) drop-shadow(0 6px 6px rgba(0,0,0,0.2))'
            : 'drop-shadow(0 6px 6px rgba(0,0,0,0.15))',
        }}
      />
      {selected && (
        <>
          <button
            data-testid={`remove-${item.slug}`}
            onPointerDown={(e) => { e.stopPropagation(); }}
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-white border border-border shadow-lg flex items-center justify-center"
            aria-label="Verwijder item"
          >
            <Trash2 className="w-3.5 h-3.5 text-destructive" />
          </button>
          <button
            data-testid={`shrink-${item.slug}`}
            onPointerDown={(e) => { e.stopPropagation(); }}
            onClick={(e) => { e.stopPropagation(); onResize(Math.max(0.5, scale - 0.2)); }}
            className="absolute -bottom-2 -left-2 w-7 h-7 rounded-full bg-white border border-border shadow-lg flex items-center justify-center"
            aria-label="Kleiner maken"
          >
            <Minus className="w-3.5 h-3.5 text-foreground" />
          </button>
          <button
            data-testid={`grow-${item.slug}`}
            onPointerDown={(e) => { e.stopPropagation(); }}
            onClick={(e) => { e.stopPropagation(); onResize(Math.min(2.5, scale + 0.2)); }}
            className="absolute -bottom-2 -right-2 w-7 h-7 rounded-full bg-white border border-border shadow-lg flex items-center justify-center"
            aria-label="Groter maken"
          >
            <Plus className="w-3.5 h-3.5 text-foreground" />
          </button>
        </>
      )}
    </motion.div>
  );
}
